<script setup lang="ts">
import { computed, useTemplateRef } from "vue";

import HtmlSnippetWidget from "@/components/ui/HtmlSnippetWidget.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import type { EstimatorReportDto } from "@/dto";
import { useClipboard } from "@/services/clipboard";
import AddToPresentationMenu from "@/views/AddToPresentationMenu.vue";

const { report } = defineProps<{ report: EstimatorReportDto }>();

const clipboard = useClipboard();

const modelElement = useTemplateRef<HTMLElement>("modelElement");

const html = computed(() => {
  const r = report.related_items.find(
    (r) => r.category === "model" && r.representation.media_type === "text/html"
  );
  return r?.representation.value;
});

async function copyModel() {
  if (modelElement.value) {
    await clipboard.copyElementAsPng(modelElement.value);
  }
}
</script>

<template>
  <div class="model">
    <div class="help">
      Review your model settings and dataset details in one place. See which hyperparameters were
      used, how many samples you trained on, and whether any preprocessing steps might affect
      performance. It’s your reference point for reproducibility.
    </div>
    <div class="block-title label-s">
      <div><i class="title-icon icon icon-bar-chart-1" /> Model Pipeline</div>
      <div class="actions">
        <AddToPresentationMenu
          name="Model Pipeline"
          :report_id="report.id"
          media_type="text/html"
          :value="html"
        />
        <SimpleButton icon-left="icon-copy" kind="tertiary" @click="copyModel" />
      </div>
    </div>
    <div class="model-container">
      <div ref="modelElement">
        <HtmlSnippetWidget :src="html" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.model {
  display: flex;
  height: 100%;
  flex-direction: column;
  margin: var(--spacing-24) 0;

  .help {
    margin-bottom: var(--spacing-12);
  }

  .model-container {
    display: flex;
    flex-grow: 1;
    align-items: center;
    justify-content: center;
    border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
    border-radius: var(--radius-s);
    background-image: radial-gradient(var(--color-stroke-background-primary) 1px, transparent 0);
    background-position: -6px -7px;
    background-size: 13px 15px;

    & > div {
      width: 80%;
    }
  }
}
</style>
